import { Text, Button, OTPInput } from '@restworld/ui-ds-base';
import { memo, useEffect } from 'react';
import { useOTPForm } from './hooks/use-otp-form';
import { AuthTitle } from '../common/auth-title';
import { AuthSubtitle } from '../common/auth-subtitle';
import { Controller } from 'react-hook-form';
import { usePhoneNumberLoginForm } from './hooks/use-phone-number-login-form';
import { useAtomValue } from 'jotai';
import { phoneNumberValidationDetails } from '../../context/auth-flow';
import { HeaderWrapper } from '../common/head-wrapper';
import { useIntercom } from 'react-use-intercom';
import { useTranslation } from 'next-i18next';
import { useAnalytics, useCountDownTimer } from '@restworld/utils-common';
import { cn } from '@restworld/utils-common';

export type VerifyOtpFormProps = {
  isModal?: boolean;
};

export const VerifyOtpForm = memo(({ isModal }: VerifyOtpFormProps) => {
  const { waitTime, phoneNumber } = useAtomValue(phoneNumberValidationDetails);
  const { show: showIntercom } = useIntercom();
  const { t } = useTranslation('auth-flow');
  const { t: commonT } = useTranslation('common');
  const {
    loading,
    onSubmit,
    handleSubmit,
    control,
    errors,
    serverValidationFailed,
    setServerValidationFailed,
    isSuccess,
  } = useOTPForm({
    phoneNumber,
    isModal,
  });

  const {
    loading: requestOTPLoading,
    onSubmit: requestNewOTPCode,
    remainingTimes,
  } = usePhoneNumberLoginForm({
    isModal,
  });

  const { hasExpired, label, resetTimer } = useCountDownTimer({ timeInSeconds: waitTime });
  const { trackEvent } = useAnalytics();

  useEffect(() => {
    if (remainingTimes > 0) {
      resetTimer();
    }
  }, [remainingTimes, resetTimer]);

  function onRequestNewOTPCode() {
    trackEvent('worker_request_otp_for_login', { resend_code: true, phone_number: phoneNumber });
    requestNewOTPCode({ phoneNumber });
  }

  return (
    <FormWrapper {...{ isModal }}>
      <form className="w-full" onSubmit={handleSubmit(onSubmit)}>
        <div className="flex flex-col items-center w-full my-10 gap-4">
          <Controller
            name="otpCode"
            control={control}
            rules={{
              required: t('phone_number_login_form_insert_valid_otp_code'),
              validate: (value) => value?.toString().length === 6,
            }}
            render={({ field }) => (
              <OTPInput
                onChange={(otp) => {
                  field.onChange(otp);
                  serverValidationFailed && setServerValidationFailed(false);
                }}
                variant={errors['otpCode'] ? 'error' : 'primary'}
                value={field?.value?.toString()}
                isSuccess={isSuccess}
              />
            )}
          />

          {(serverValidationFailed || errors['otpCode']) && (
            <p className="text-base leading-6 text-red-600 text-center">
              {serverValidationFailed && (
                <span>
                  {t('phone_number_login_form_otp_assistance_text_1')}{' '}
                  <StyledLink
                    onClick={(e) => {
                      e.preventDefault();
                      showIntercom();
                      // trackEvent('Auth flow', {
                      //   phase: 'Phone number login',
                      //   step: 'contact assistance',
                      //   mode: logMode,
                      // });
                    }}
                  >
                    {t('phone_number_login_form_otp_assistance_text_2')}
                  </StyledLink>
                </span>
              )}
              {/* Wrapped, not bare: browser translation moves a bare text node into <font>, and
                  when this message changed (re-validation on typing) or gave way to the span
                  above, React's removeChild against the moved node crashed the page (prod
                  01/10/2026, translated session). Same fix as the similar-jobs label (#417). */}
              {errors['otpCode'] && !serverValidationFailed && <span>{errors['otpCode']?.message}</span>}
            </p>
          )}
        </div>
        <div className="flex flex-col items-center w-full gap-4">
          <Button fullwidth={true} type="submit" loading={loading}>
            <span className="text-black">{commonT('login')}</span>
          </Button>
          <div className="flex flex-col items-center w-full gap-2">
            <Button
              fullwidth={true}
              disabled={!hasExpired || remainingTimes === 0}
              variant="secondary-neutral"
              loading={requestOTPLoading}
              onClick={() => onRequestNewOTPCode()}
            >
              <span className="text-black">{`${t('phone_number_login_form_otp_resend_text_1')}  ${
                !hasExpired ? `${t('phone_number_login_form_otp_resend_text_2')} ${label}` : ''
              }`}</span>
            </Button>
            {remainingTimes < 3 && (
              <Text color="muted">
                {remainingTimes} {t('phone_number_login_form_otp_sent_count')}
              </Text>
            )}
          </div>
        </div>
      </form>
    </FormWrapper>
  );
});

const FormWrapper = ({
  isModal = true,
  className,
  ...props
}: { isModal?: boolean } & React.HTMLAttributes<HTMLElement>) => (
  <div className={cn('flex flex-col w-full', isModal && 'mt-5', className)} {...props} />
);

export const VerifyOtpHeader = ({ isModal = true }: { isModal?: boolean }) => {
  const { phoneNumber } = useAtomValue(phoneNumberValidationDetails);
  const { t } = useTranslation('auth-flow');
  return (
    <HeaderWrapper className="gap-2 mt-2" isModal={isModal}>
      <AuthTitle>{t('phone_number_login_form_otp_header')}</AuthTitle>
      <AuthSubtitle>
        {t('phone_number_login_form_otp_subheader')} {phoneNumber}
      </AuthSubtitle>
    </HeaderWrapper>
  );
};

const StyledLink = ({ className, ...props }: React.HTMLAttributes<HTMLElement>) => (
  <p className={cn('text-red-600 underline cursor-pointer', className)} {...props} />
);
